// ---------------------------------------------------------------------------
// Simple inline Markdown-to-HTML renderer
// Handles: **bold**, \n, - list items, ### headings, [N] citation refs
// No external dependencies.
// ---------------------------------------------------------------------------

import DOMPurify from 'dompurify';

/**
 * Convert a simple markdown string to sanitized HTML.
 * Handles:
 * - `**bold**` -> `<strong>bold</strong>`
 * - `\n` -> paragraph breaks (collapsed, not excessive)
 * - `- list item` -> `<li>list item</li>` wrapped in `<ul>`
 * - `### Heading` -> `<h3>Heading</h3>` (h1-h6)
 * - `[N]` citation references are kept as-is
 */
export function renderMarkdown(md: string): string {
  if (!md) return '';

  const lines = md.split('\n');
  const htmlLines: string[] = [];
  let inList = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Heading detection: ### Heading
    const headingMatch = /^(#{1,6})\s+(.+)$/.exec(trimmed);
    if (headingMatch) {
      if (inList) {
        htmlLines.push('</ul>');
        inList = false;
      }
      const level = (headingMatch[1] ?? '').length;
      const text = applyInline(headingMatch[2] ?? '');
      htmlLines.push(`<h${level.toString()}>${text}</h${level.toString()}>`);
      continue;
    }

    // List item detection: - item or * item
    const listMatch = /^[-*]\s+(.+)$/.exec(trimmed);
    if (listMatch) {
      if (!inList) {
        htmlLines.push('<ul>');
        inList = true;
      }
      htmlLines.push(`<li>${applyInline(listMatch[1] ?? '')}</li>`);
      continue;
    }

    // Not a list item — close list if open
    if (inList) {
      htmlLines.push('</ul>');
      inList = false;
    }

    // Empty line — skip (headings/lists/paragraphs handle their own spacing)
    if (trimmed === '') {
      continue;
    }

    // Regular text line — wrap in <p>
    htmlLines.push(`<p>${applyInline(trimmed)}</p>`);
  }

  if (inList) {
    htmlLines.push('</ul>');
  }

  const result = htmlLines.join('\n');
  return DOMPurify.sanitize(result);
}

/** Apply inline formatting: **bold** */
function applyInline(text: string): string {
  // Bold: **text**
  let result = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Italic: *text* (but not inside **)
  result = result.replace(
    /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g,
    '<em>$1</em>',
  );
  return result;
}

/** Check if a string looks like markdown (has markdown patterns, no HTML tags) */
export function isMarkdownContent(text: string): boolean {
  const hasHtml = /<[a-z][\s\S]*>/i.test(text);
  if (hasHtml) return false;
  // Check for markdown patterns
  return (
    /\*\*.+?\*\*/.test(text) ||
    /^#{1,6}\s+/m.test(text) ||
    /^[-*]\s+/m.test(text)
  );
}
